import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import DealDetailView from "../components/Deals/DealDetailView";
import DealForm from "../components/Deals/DealForm";
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  Card,
  Modal,
  Progress,
  Tooltip,
  App,
} from "antd";
import {
  PlusOutlined,
  FilterOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import useDealStore from "../store/dealStore";
import useAuthStore from "../store/authStore";
import useSettingsStore from "../store/settingsStore";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { Option } = Select;

const DealsPage = () => {
  return (
    <Routes>
      <Route index element={<DealsList />} />
      <Route path="new" element={<DealForm />} />
      <Route path=":id" element={<DealDetailView />} />
      <Route path=":id/edit" element={<DealForm />} />
    </Routes>
  );
};

const DealsList = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const {
    deals,
    isLoading,
    pagination,
    filters,
    fetchDeals,
    deleteDeal,
    setFilters,
    clearFilters,
  } = useDealStore();
  const { formatCurrency } = useSettingsStore();

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [dealToDelete, setDealToDelete] = useState(null);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const handleSearch = (value) => {
    setFilters({ search: value });
    fetchDeals({ search: value, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { [key]: value };
    setFilters(newFilters);
    fetchDeals({ ...newFilters, page: 1 });
  };

  const handleTableChange = (paginationInfo, filtersInfo, sorter) => {
    const params = {
      page: paginationInfo.current,
      limit: paginationInfo.pageSize,
      ...filtersInfo,
    };

    if (sorter.field) {
      params.sort = sorter.field;
      params.order = sorter.order === "ascend" ? "asc" : "desc";
    }

    fetchDeals(params);
  };

  const handleDelete = async (dealId) => {
    const result = await deleteDeal(dealId);
    if (result.success) {
      message.success("Deal deleted successfully");
      setDeleteModalVisible(false);
      setDealToDelete(null);
    } else {
      message.error(result.error || "Failed to delete deal");
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      prospecting: "geekblue",
      qualification: "blue",
      needs_analysis: "cyan",
      proposal: "orange",
      negotiation: "purple",
      closed_won: "success",
      closed_lost: "error",
    };
    return colors[stage] || "default";
  };

  const getStageProgress = (stage) => {
    const stageProgress = {
      prospecting: 10,
      qualification: 25,
      needs_analysis: 40,
      proposal: 60,
      negotiation: 80,
      closed_won: 100,
      closed_lost: 0,
    };
    return stageProgress[stage] || 0;
  };

  const columns = [
    {
      title: "Deal",
      key: "deal",
      render: (_, record) => ( 
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
            <DollarOutlined className="text-green-600" />
          </div>
          <div>
            <Text strong className="block">
              {record.title}
            </Text>
            <Text className="text-sm text-gray-500">{record.contactName}</Text>
          </div>
        </div>
      ),
      sorter: true,
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      sorter: true,
      render: (_, record) => ( 
        <div className="flex items-center space-x-3">
          <div>
            <Text strong className="block">
              {record.contact.company ? record.contact.company : " - "}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (value) => (
        <Text strong className="text-green-600">
          {formatCurrency(value)}
        </Text>
      ),
      sorter: true,
    },
    {
      title: "Stage",
      dataIndex: "stage",
      key: "stage",
      render: (stage) => (
        <div className="space-y-1">
          <Tag color={getStageColor(stage)}>
            {stage?.replace("_", " ").toUpperCase()}
          </Tag>
          <Progress
            percent={getStageProgress(stage)}
            size="small"
            showInfo={false}
            strokeColor={
              getStageColor(stage) === "success" ? "#52c41a" : "#1890ff"
            }
          />
        </div>
      ),
      filters: [
        { text: "Prospecting", value: "prospecting" },
        { text: "Qualification", value: "qualification" },
        { text: "Needs Analysis", value: "needs_analysis" },
        { text: "Proposal", value: "proposal" },
        { text: "Negotiation", value: "negotiation" },
        { text: "Closed Won", value: "closed_won" },
        { text: "Closed Lost", value: "closed_lost" },
      ],
    },
    {
      title: "Probability",
      dataIndex: "probability",
      key: "probability",
      render: (probability) => (
        <div className="flex items-center space-x-2">
          <Progress
            type="circle"
            percent={probability}
            size={40}
            strokeColor={
              probability >= 75
                ? "#52c41a"
                : probability >= 50
                ? "#faad14"
                : "#ff4d4f"
            }
          />
          {/* <Text>{probability}%</Text> */}
        </div>
      ),
      sorter: true,
    },
    {
      title: "Expected Close",
      dataIndex: "expectedCloseDate",
      key: "expectedCloseDate",
      render: (date) => (
        <Text>{date ? dayjs(date).format("MMM DD, YYYY") : "Not set"}</Text>
      ),
      sorter: true,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => (
        <Tooltip title={dayjs(date).format("MMMM DD, YYYY HH:mm")}>
          <Text className="text-sm text-gray-500">
            {dayjs(date).format("MMM DD, YYYY")}
          </Text>
        </Tooltip>
      ),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/deals/${record._id}`);
            }}
            className="focus-visible:focus relative z-10"
            aria-label={`View ${record.title}`}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/deals/${record._id}/edit`);
            }}
            className="focus-visible:focus relative z-10"
            aria-label={`Edit ${record.title}`}
          />
          {user?.role === 'tenant_admin' && (
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setDealToDelete(record);
                setDeleteModalVisible(true);
              }}
              className="focus-visible:focus relative z-10"
              aria-label={`Delete ${record.title}`}
            />
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Title level={2} className="mb-2">
            Deals
          </Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Manage your sales pipeline and deals
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/deals/new")}
          className="touch-target focus-visible:focus"
        >
          Create Deal
        </Button>
      </div>

      {/* Filters */}
      <Card className="shadow-soft">
        <div className="flex flex-wrap gap-4 items-center">
          <Input.Search
            placeholder="Search deals..."
            allowClear
            onSearch={handleSearch}
            value={filters.search || ''}
            onChange={(e) => {
              setFilters({ search: e.target.value });
            }}
            style={{ width: 300 }}
            className="focus-visible:focus"
          />

          <Select
            placeholder="Stage"
            allowClear
            style={{ width: 150 }}
            onChange={(value) => handleFilterChange("stage", value)}
            value={filters.stage}
            className="focus-visible:focus"
          >
            <Option value="qualification">Qualification</Option>
            <Option value="needs_analysis">Needs Analysis</Option>
            <Option value="proposal">Proposal</Option>
            <Option value="negotiation">Negotiation</Option>
            <Option value="closed_won">Closed Won</Option>
            <Option value="closed_lost">Closed Lost</Option>
          </Select>

          <Button
            icon={<FilterOutlined />}
            onClick={() => {
              clearFilters();
              fetchDeals({
                page: 1,
                stage: null,
                assignedTo: null,
                source: null,
                priority: null,
                search: ''
              });
            }}
            className="focus-visible:focus"
          >
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card className="shadow-soft">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={deals}
          rowKey="_id"
          loading={isLoading}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} deals`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          onRow={(record) => ({
            onClick: () => navigate(`/deals/${record._id}`),
            style: { cursor: "pointer" },
            className: "hover:bg-gray-50 transition-colors duration-200",
          })}
        />
      </Card>

      {/* Delete Modal */}
      <Modal
        title="Delete Deal"
        open={deleteModalVisible}
        onOk={() => handleDelete(dealToDelete?._id)}
        onCancel={() => {
          setDeleteModalVisible(false);
          setDealToDelete(null);
        }}
        okText="Delete"
        okType="danger"
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to delete <strong>{dealToDelete?.title}</strong>
          ? This action cannot be undone.
        </p>
      </Modal>
    </div>
  );
};

export default DealsPage;
